import { useEffect } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { AutoNavigateFn, FlowContextProvider, StepProps, StepWrapper, useAutoNavigate } from '@betterment/js-runtime';
import { SuccessFullScreen } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';
import { Route, Routes, useMatch, useSearchParams } from 'react-router-dom';

import { DirectTradingAccountCreationFlowDocument } from './graphql/query.directTradingAccountCreationFlow.generated';
import { InitialFundingStep } from './steps/initial-funding/InitialFundingStep';
import { IntroductionStep } from './steps/IntroductionStep';
import { InvestingExperienceStep } from './steps/InvestingExperienceStep';
import TermsAndAgreementsStep from './steps/TermsAndAgreementsStep';
import { UserLevelSettingsStep } from './steps/UserLevelSettingsStep';

import { InvestingExperienceLevel, SecuritiesLendingStatus } from '../graphql/schema';
import { IdempotencyProviderWithFeatureRegistry } from '../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { urlBuilder } from '../Shared/urlBuilder';

export type DirectTradingFlowStateValues = {
  investingExperience: InvestingExperienceLevel;
  isSecurityLendingEnrolled: boolean | null;
  accountId: string | null;
};

const validateUserIsNotSuspended = (isSuspended: boolean, navigate: AutoNavigateFn): void => {
  if (!isSuspended) return;

  void navigate(urlBuilder.appHome.absolute(), { replace: true });
};
const validateProfileSetupRequired = (
  isProfileSetupRequired: boolean,
  productCategory: string | null,
  navigate: AutoNavigateFn
): void => {
  if (!isProfileSetupRequired) return;
  const profileSetupUrl = urlBuilder.appProfileSetupFlow.absolute({
    ...(productCategory && { product_category: productCategory }),
  });
  void navigate(profileSetupUrl, { replace: true });
};

export const shouldShowUserLevelSettingsStep = (
  status: SecuritiesLendingStatus,
  canUserSelfUpdate: boolean
): boolean => {
  return (
    ![SecuritiesLendingStatus.Ineligible, SecuritiesLendingStatus.Unknown, SecuritiesLendingStatus.Enrolled].includes(
      status
    ) && canUserSelfUpdate
  );
};

export default function Controller() {
  const match = useMatch(`${urlBuilder.appAddAccount()}/*`);
  const returnToPath = urlBuilder.appHome.absolute();
  const navigate = useAutoNavigate();

  const [searchParams] = useSearchParams();
  const productCategory = searchParams.get('product_category');

  const { data } = useGraphQLQuery(DirectTradingAccountCreationFlowDocument, {
    throwOnError: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data) return;

    validateUserIsNotSuspended(data.user.isSuspended, navigate);
    validateProfileSetupRequired(data.user.isProfileSetupRequired, productCategory, navigate);
  }, [data, productCategory, navigate]);

  if (!data || data.user.isProfileSetupRequired || data.user.isSuspended) return <FullPageLoader />;

  const {
    financialProfile,
    goalCreationOption: { showInvestingExperience },
    userLevelSettings: { securityLending },
  } = data;

  const initialFlowValues = {
    investingExperience: financialProfile?.investingExperience || InvestingExperienceLevel.New,
    isSecurityLendingEnrolled: null,
    accountId: null,
  };

  const onSuccessCompletion = (stepProps: StepProps<DirectTradingFlowStateValues>) => {
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('onComplete');
    } else {
      stepProps.goToNextStep();
    }
  };

  return (
    <ErrorBoundary fallbackType="full" feature="self_directed_investing" owner="user_engagement">
      <IdempotencyProviderWithFeatureRegistry featureName="createDirectTradingAccount">
        <FlowContextProvider<DirectTradingFlowStateValues>
          basePath={match!.pathnameBase}
          routePath=""
          returnToPath={returnToPath}
          initialValues={initialFlowValues}
        >
          <Routes>
            <Route
              path="/introduction"
              element={
                <StepWrapper name="introduction">{stepProps => <IntroductionStep {...stepProps} />}</StepWrapper>
              }
            />
            {showInvestingExperience && (
              <Route
                path="/investing_experience"
                element={
                  <StepWrapper<DirectTradingFlowStateValues> name="investing_experience">
                    {stepProps => <InvestingExperienceStep {...stepProps} />}
                  </StepWrapper>
                }
              />
            )}
            {shouldShowUserLevelSettingsStep(securityLending.status, securityLending.canUserSelfUpdate) && (
              <Route
                path="/user_level_settings"
                element={
                  <StepWrapper<DirectTradingFlowStateValues> name="user_level_settings">
                    {stepProps => <UserLevelSettingsStep {...stepProps} />}
                  </StepWrapper>
                }
              />
            )}
            <Route
              path="/terms_and_agreements"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="terms_and_agreements">
                  {stepProps => <TermsAndAgreementsStep {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              path="/success"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="success">
                  {stepProps => (
                    <SuccessFullScreen
                      message="Your account setup is complete"
                      onCompletion={() => onSuccessCompletion(stepProps)}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/initial_funding"
              element={
                <StepWrapper<DirectTradingFlowStateValues> name="initial_funding">
                  {stepProps => <InitialFundingStep {...stepProps} />}
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </IdempotencyProviderWithFeatureRegistry>
    </ErrorBoundary>
  );
}
